<script setup lang="ts">
const modelValue = defineModel<string>('modelValue', {
  default: '',
});

const emit = defineEmits(['submit']);

const inputsLength = ref<number>(5);
const wrapperElement = ref<HTMLDivElement | null>(null);
const inputElements = computed<HTMLInputElement[]>(() => {
  if (!wrapperElement.value) {
    return [];
  }

  return [
    ...wrapperElement.value.querySelectorAll<HTMLInputElement>(
      'input[type="tel"]',
    ),
  ];
});

function updateModelValue() {
  if (!inputElements.value.length) {
    modelValue.value = '';

    return;
  }

  modelValue.value = inputElements.value.map(({ value }) => value).join('');
}

function onKeyDown(event: KeyboardEvent, index: number) {
  if (
    event.key === 'Backspace' &&
    (event.target as HTMLInputElement).value === ''
  ) {
    inputElements.value[Math.max(0, index - 1)]?.focus();
  }
}

function populateInputElements(value: string | string[], index: number) {
  if (!value.length || index < 0) {
    return;
  }

  if (!inputElements.value[index]) {
    return;
  }

  inputElements.value[index].focus();

  const [first, ...rest] = value;

  inputElements.value[index].value = first ?? '';

  updateModelValue();

  const nextInputIndex = index + 1;

  if (rest.length) {
    populateInputElements(rest, nextInputIndex);
  } else {
    if (nextInputIndex >= inputsLength.value) {
      return;
    }

    inputElements.value[nextInputIndex]?.select();
  }
}

function onInput(event: InputEvent, index: number) {
  updateModelValue();

  if (!event.data) {
    return;
  }

  populateInputElements(event.data, index);
}

watch(modelValue, (value) => {
  if (value.length === 5) {
    emit('submit');
  }
});

onActivated(() => {
  updateModelValue();
});

onMounted(() => {
  updateModelValue();

  nextTick(() => {
    if (!inputElements.value.length) {
      return;
    }

    inputElements.value[0]?.focus();
  });
});
</script>

<template>
  <client-only>
    <div
      ref="wrapperElement"
      class="w-11/12 md:w-10/12 mx-auto flex items-center justify-center gap-3 md:gap-4"
    >
      <input
        v-for="inputNumber in inputsLength"
        :key="inputNumber"
        autocomplete="off"
        :class="$style.codeInput"
        name="code"
        required
        type="tel"
        @click="(event: Event) => (event.target as HTMLInputElement)?.select()"
        @input="(event: Event) => onInput(event as InputEvent, inputNumber - 1)"
        @keydown="
          (event: Event) => onKeyDown(event as KeyboardEvent, inputNumber - 1)
        "
      />
    </div>
  </client-only>
</template>

<style module lang="postcss">
.codeInput {
  @apply text-xl text-center w-6 max-w-16 h-14 flex-1 bg-white/10 rounded-lg text-texts/80 border-2 border-transparent border-solid focus:outline-none focus:ring-0 focus:border-2 focus:bg-black focus:border-primary select-none caret-transparent;

  &::selection {
    @apply bg-transparent;
  }
}
</style>
